/**
 * 认证 REST API 服务
 *
 * 封装认证相关的 REST API 调用，使用 HttpOnly Cookie 存储 JWT Token。
 * 所有请求使用 `credentials: 'include'` 以自动携带 Cookie。
 */

import type { User, SubscriptionType } from '../types/auth';

// API基础URL配置
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// HTTP错误类
export class AuthApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'AuthApiError';
  }
}

export class UnauthorizedError extends AuthApiError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class ValidationError extends AuthApiError {
  constructor(message: string) {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class ConflictError extends AuthApiError {
  constructor(message: string) {
    super(message, 409, 'CONFLICT');
    this.name = 'ConflictError';
  }
}

/**
 * 处理HTTP响应错误
 */
async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // 无法解析JSON，使用默认错误消息
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage);
    case 403:
      throw new AuthApiError(errorMessage, 403, 'FORBIDDEN');
    case 409:
      throw new ConflictError(errorMessage);
    default:
      throw new AuthApiError(errorMessage, response.status);
  }
}

/**
 * 用户信息接口（API响应）
 */
export interface UserResponse {
  id: number;
  email: string;
  phone?: string | null;
  has_password?: boolean;
  nickname: string | null;
  level: string;
  subscription_type: SubscriptionType;
  is_verified: boolean;
  created_at: string;
  last_login_at: string | null;
  is_admin?: boolean;
}

/**
 * 认证响应接口
 */
export interface AuthResponse {
  user: UserResponse;
  message: string;
  access_token?: string;  // JWT Token，用于 WebSocket 连接
}

/**
 * 通用消息响应
 */
export interface MessageResponse {
  message: string;
}

/**
 * 注册响应（需要验证）
 */
export interface RegisterResponse {
  message: string;
  email: string;
  requires_verification: boolean;
}

/**
 * 验证码验证请求
 */
export interface VerifyEmailCodeRequest {
  email: string;
  code: string;
}

/**
 * 验证成功响应
 */
export interface VerifyEmailResponse {
  message: string;
  token?: string;
  user?: UserResponse;
}

/**
 * 重发验证邮件请求
 */
export interface ResendVerificationRequest {
  email: string;
}

/**
 * 登录请求
 */
export interface LoginRequest {
  email: string;
  password: string;
}

/**
 * 注册请求
 */
export interface RegisterRequest {
  email: string;
  password: string;
  password_confirm: string;
  nickname?: string;
  captcha_token?: string;
}

/**
 * 认证API服务类
 */
export class AuthApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * 用户登录
   *
   * @param email - 邮箱地址
   * @param password - 密码
   * @returns 用户信息
   */
  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // 携带 Cookie
      body: JSON.stringify({ email, password } as LoginRequest),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 用户注册
   *
   * @param email - 邮箱地址
   * @param password - 密码
   * @param passwordConfirm - 确认密码
   * @param nickname - 昵称（可选）
   * @returns 注册响应（需要验证）
   */
  async register(
    email: string,
    password: string,
    passwordConfirm: string,
    nickname?: string,
    captchaToken?: string | null
  ): Promise<RegisterResponse> {
    const requestData: RegisterRequest = {
      email,
      password,
      password_confirm: passwordConfirm,
    };

    if (nickname) {
      requestData.nickname = nickname;
    }

    if (captchaToken) {
      requestData.captcha_token = captchaToken;
    }

    const response = await fetch(`${this.baseUrl}/api/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 通过验证码验证邮箱
   *
   * @param email - 邮箱地址
   * @param code - 6位验证码
   * @returns 验证成功响应
   */
  async verifyEmailCode(email: string, code: string): Promise<VerifyEmailResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/verify-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, code } as VerifyEmailCodeRequest),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 通过验证链接验证邮箱
   *
   * @param token - UUID验证令牌
   * @returns 验证成功响应
   */
  async verifyEmailToken(token: string): Promise<VerifyEmailResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/verify-email/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 重发验证邮件
   *
   * @param email - 邮箱地址
   * @returns 成功消息
   */
  async resendVerification(email: string, captchaToken?: string | null): Promise<MessageResponse> {
    const body: any = { email };
    if (captchaToken) {
      body.captcha_token = captchaToken;
    }

    const response = await fetch(`${this.baseUrl}/api/auth/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 用户登出
   *
   * @returns 成功消息
   */
  async logout(): Promise<MessageResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 获取当前登录用户信息
   *
   * 用于初始化时检查登录状态，Cookie 自动携带。
   * @returns 用户信息，未登录时抛出 UnauthorizedError
   */
  async getCurrentUser(): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 检查登录状态
   *
   * @returns 是否已登录
   */
  async checkAuthStatus(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * 获取用户资料
   *
   * @returns 用户资料
   */
  async getProfile(): Promise<UserResponse> {
    const response = await fetch(`${this.baseUrl}/api/user/profile`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // 携带 Cookie
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 更新用户资料
   *
   * @param nickname - 新昵称
   * @returns 更新后的用户资料
   */
  async updateProfile(nickname: string): Promise<UserResponse> {
    const response = await fetch(`${this.baseUrl}/api/user/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // 携带 Cookie
      body: JSON.stringify({ nickname }),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 发送短信验证码（需先通过滑块验证）
   *
   * @param phone - 中国大陆手机号
   * @param captchaToken - 滑块验证令牌
   * @returns 发送结果（有效期/间隔）
   */
  async smsSend(phone: string, captchaToken: string): Promise<{ message: string; expire_minutes: number; interval_seconds: number }> {
    const response = await fetch(`${this.baseUrl}/api/auth/sms/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ phone, captcha_token: captchaToken }),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 手机号验证码登录/注册（未注册自动创建账号）
   *
   * @param phone - 中国大陆手机号
   * @param code - 短信验证码
   * @returns 登录响应（含用户信息与JWT）
   */
  async smsLogin(phone: string, code: string): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/api/auth/sms/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ phone, code }),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }
  /**
   * 绑定手机号（需先获取短信验证码）
   *
   * @param phone - 中国大陆手机号
   * @param code - 短信验证码
   * @returns 更新后的用户资料
   */
  async bindPhone(phone: string, code: string): Promise<UserResponse> {
    const response = await fetch(`${this.baseUrl}/api/user/phone/bind`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ phone, code }),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }

  /**
   * 设置/修改密码（手机号注册用户首次设置无需旧密码）
   *
   * @param newPassword - 新密码
   * @param newPasswordConfirm - 确认新密码
   * @param oldPassword - 当前密码（已有密码时必填）
   */
  async setPassword(
    newPassword: string,
    newPasswordConfirm: string,
    oldPassword?: string
  ): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/user/password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
        old_password: oldPassword,
      }),
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  }
}

// 导出单例实例
export const authApi = new AuthApiService();

/**
 * 将 API 响应的用户信息转换为前端 User 类型
 */
export function toUser(response: UserResponse): User {
  return {
    id: response.id,
    email: response.email,
    phone: response.phone,
    nickname: response.nickname,
    level: response.level,
    subscription_type: response.subscription_type,
    is_verified: response.is_verified,
    created_at: response.created_at,
    last_login_at: response.last_login_at ?? undefined,
    is_admin: response.is_admin ?? false,
  };
}

export default authApi;
